import { createFileRoute, Link } from "@tanstack/react-router";
import { Check, ArrowLeft } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { seoHead } from "@/lib/seo";
import { SHOW_TRIAL_PLAN } from "@/lib/featureFlags";

export const Route = createFileRoute("/pricing")({
  head: () => {
    const base = seoHead({
      title: "料金プラン - KAGEM",
      description:
        "KAGEMの料金プラン一覧。月額Light/Standard/Proの3プラン。クレジット制でムダなく使えます。",
      path: "/pricing",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  component: PricingPage,
});

const allPlans = [
  {
    name: "お試し",
    price: "¥500",
    suffix: "",
    desc: "まず音声付き高画質を1本試したい方に",
    features: ["高画質1080p動画 1本", "音声付き / 10秒", "1人1回限り"],
    planId: "trial",
    highlight: false,
  },
  {
    name: "Light",
    price: "¥2,980",
    suffix: "/月",
    desc: "趣味でたまに作る方に",
    features: ["月300クレジット", "標準品質", "ウォーターマーク無し", "商用利用OK"],
    planId: "light",
    highlight: false,
  },
  {
    name: "Standard",
    price: "¥8,800",
    suffix: "/月",
    desc: "定期的にSNS投稿する方に",
    features: ["月1,000クレジット", "高品質モデル", "優先処理", "商用利用OK"],
    planId: "standard",
    highlight: true,
  },
  {
    name: "Pro",
    price: "¥24,800",
    suffix: "/月",
    desc: "本格制作のクリエイターに",
    features: ["月3,000クレジット", "全AIモデル", "最優先処理", "API連携"],
    planId: "pro",
    highlight: false,
  },
];

const plans = allPlans.filter((p) => SHOW_TRIAL_PLAN || p.planId !== "trial");

const faqs = [
  {
    q: "クレジットってなんですか？",
    a: "動画や画像を1本生成するごとに消費するポイントです。品質や秒数によって消費量が変わります。",
  },
  {
    q: "クレジットの有効期限は？",
    a: "サブスクリプションで付与されるクレジットは当月末まで、購入クレジットは購入から180日間有効です。",
  },
  {
    q: "解約はいつでもできますか？",
    a: "月契約はいつでも解約可能で、当月分は最後までご利用いただけます。年契約（12ヶ月縛りの月払い）は解約リクエストをいつでも受け付けますが、12ヶ月目の課金完了まで請求が継続し、その終了日に自動解約されます。違約金は一切ありません。",
  },
  {
    q: "商用利用できますか？",
    a: "Standardプラン以上で商用利用が可能です。SNS投稿、広告、クライアントワークなどにご利用ください。",
  },
  {
    q: "プラン変更はできますか？",
    a: "はい、いつでもアップグレード/ダウングレードできます。日割り計算で差額を調整します。",
  },
];

function PricingPage() {
  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-6xl px-4 py-12 sm:py-20">
        <div className="mb-8">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/">
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              ホームへ戻る
            </Link>
          </Button>
        </div>

        <header className="mb-12 text-center sm:mb-16">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">料金プラン</h1>
          <p className="mt-4 text-muted-foreground">
            用途に合わせて選べるプラン。月契約はいつでも変更・解約OK。
          </p>
        </header>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {plans.map((p) => (
            <Card
              key={p.name}
              className={`relative flex h-full flex-col p-6 transition-all ${
                p.highlight
                  ? "border-primary shadow-xl shadow-primary/20 lg:scale-105"
                  : "border-border/60 hover:-translate-y-1 hover:shadow-lg"
              }`}
            >
              {p.highlight && (
                <Badge className="bg-gradient-brand absolute -top-3 left-1/2 -translate-x-1/2 border-0 px-3">
                  おすすめ
                </Badge>
              )}
              <h2 className="text-lg font-bold">{p.name}</h2>
              <p className="mt-1 text-xs text-muted-foreground">{p.desc}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-extrabold">{p.price}</span>
                <span className="text-sm text-muted-foreground">{p.suffix}</span>
              </div>
              <ul className="mt-6 flex-1 space-y-2.5">
                {p.features.map((feat) => (
                  <li key={feat} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
              <Button
                asChild
                className={`mt-6 w-full ${p.highlight ? "bg-gradient-brand" : ""}`}
                variant={p.highlight ? "default" : "outline"}
              >
                <Link to="/signup" search={{ plan: p.planId } as never}>
                  このプランで始める
                </Link>
              </Button>
            </Card>
          ))}
        </div>

        <section className="mx-auto mt-20 max-w-3xl">
          <h2 className="mb-8 text-center text-2xl font-bold sm:text-3xl">よくある質問</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left text-base font-medium">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        <section className="mt-16 text-center">
          <p className="text-sm text-muted-foreground">
            ご不明点は{" "}
            <Link to="/contact" className="text-primary hover:underline">
              お問い合わせ
            </Link>{" "}
            までお気軽にどうぞ。
          </p>
        </section>
      </div>
    </main>
  );
}
